import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
    IoChevronDown, IoCloseOutline, IoCodeSlashOutline, IoDiamondOutline, IoGridOutline, IoLogOutOutline, IoMenuOutline, IoSparklesOutline, IoRocketOutline,
    IoMoonOutline, IoReceiptOutline, IoShieldCheckmarkOutline, IoStorefrontOutline, IoSunnyOutline, IoSwapHorizontalOutline
} from 'react-icons/io5'
import './Portal.css'
import './UnitNav.css'

// ─────────────────────────────────────────────────────────────────────────────
//  NAVBAR DA UNIDADE — padrão novo
//
//  Os grupos vêm de App.unitNavGroups (a definição única). Cada item tem
//  ícone e uma linha dizendo PARA QUE serve — quem não conhece o sistema
//  entende o menu sem clicar em tudo. No celular vira uma gaveta com as
//  mesmas seções.
//
//  Grupo com um item só vira link direto; `soon`/`locked` são vitrine: sem
//  `upgrade` não têm link; com `upgrade` levam à tela que explica o recurso e
//  como liberar (Portal/plano/Upgrade). Grupo só de vitrine aparece na barra
//  quando algum item leva ao upgrade (igual à gaveta do celular); sem nenhum
//  link, fica só na gaveta.
//
//  modo="admin": a mesma barra no Admin Console (App.adminNavGroups) — o logo
//  leva a /listlogins, sem pílula de plano, e a conta é a "Equipe BIWAPP"
//  com "Entrar em uma unidade" e "Sair".
//
//  modo="pool": o login que acompanha várias unidades — o logo leva à Visão
//  geral (/overview), a conta é o pool (nome do login e quantas unidades) com
//  "Entrar em uma unidade" e "Sair".
// ─────────────────────────────────────────────────────────────────────────────

const ativoEm = (item, pathname) => {
    if (item.soon || item.locked) return false
    const alvo = item.to.split('?')[0]
    if (item.match) return item.match.some((m) => pathname === m || pathname.startsWith(m + '/'))
    return alvo === '/' ? pathname === '/' : pathname === alvo || pathname.startsWith(alvo + '/')
}

function ItemMenu({ item, onNavegar, grande }) {
    const Icone = item.icon
    const conteudo = (
        <>
            <span className="un-item-ic">{Icone && <Icone size={grande ? 20 : 18} />}</span>
            <span className="un-item-txt">
                <strong>
                    {item.label}
                    {item.soon && <em className="un-tag">em breve</em>}
                    {item.locked && <em className="un-tag lock">não contratado</em>}
                </strong>
                {item.desc && <span>{item.desc}</span>}
            </span>
        </>
    )
    if ((item.soon || item.locked) && item.upgrade) return <Link to={item.upgrade} className="un-item off vitrine" onClick={() => onNavegar(item)}>{conteudo}</Link>
    if (item.soon || item.locked) return <div className="un-item off" aria-disabled="true">{conteudo}</div>
    return <Link to={item.to} className={'un-item' + (item.ativo ? ' ativo' : '')} onClick={() => onNavegar(item)}>{conteudo}</Link>
}

// Menu suspenso de um grupo (desktop)
function Grupo({ grupo, aberto, onAbrir, onFechar, onNavegar }) {
    const navegaveis = grupo.itens.filter((i) => !i.soon && !i.locked)
    if (grupo.itens.length === 1 && navegaveis.length === 1) {
        const i = grupo.itens[0]
        return <Link to={i.to} className={'un-top' + (i.ativo ? ' ativo' : '')} onClick={() => onNavegar(i)}>{i.solo || i.label}</Link>
    }
    const ativo = navegaveis.some((i) => i.ativo)
    return (
        <div className={'un-grupo' + (aberto ? ' aberto' : '')} onMouseLeave={onFechar}>
            <button type="button" className={'un-top' + (ativo ? ' ativo' : '')} aria-expanded={aberto}
                onClick={() => (aberto ? onFechar() : onAbrir())}>
                {grupo.title} <IoChevronDown size={13} className="un-seta" />
            </button>
            {aberto && (
                <div className="un-painel" role="menu">
                    {grupo.itens.map((i) => (
                        <React.Fragment key={i.key}>
                            {i.divider && <div className="un-div" />}
                            <ItemMenu item={i} onNavegar={onNavegar} />
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    )
}

// A barra completa só cabe em tela larga; abaixo disso vira o botão de menu
// (a gaveta) — no tablet e no notebook pequeno os grupos não se embolam.
const COMPACTO = '(max-width: 1080px)'
function useCompacto(isMobile) {
    const [c, setC] = useState(() => isMobile || (window.matchMedia && window.matchMedia(COMPACTO).matches))
    useEffect(() => {
        if (!window.matchMedia) return undefined
        const mq = window.matchMedia(COMPACTO)
        const f = () => setC(isMobile || mq.matches)
        f()
        if (mq.addEventListener) mq.addEventListener('change', f); else mq.addListener(f)
        return () => { if (mq.removeEventListener) mq.removeEventListener('change', f); else mq.removeListener(f) }
    }, [isMobile])
    return c
}

// o Início grava o nome do plano (vindo da API) para a unidade atual
function usePlanoAtual(inicial) {
    const [p, setP] = useState(inicial)
    useEffect(() => { setP(inicial) }, [inicial])
    useEffect(() => {
        const f = (e) => { if (e.detail) setP(e.detail) }
        window.addEventListener('biwapp:plano', f)
        return () => window.removeEventListener('biwapp:plano', f)
    }, [])
    return p
}

export default function UnitNav({
    grupos, isMobile: celular, isDarkMode, onToggleTheme, onLogout, onSelect,
    plano: planoInicial, podeTrocarUnidade, onTrocarUnidade, isAdmin, onAdminConsole, modo
}) {
    const ehConsole = modo === 'admin'
    const ehPool = modo === 'pool'
    const semLoja = ehConsole || ehPool
    const { pathname } = useLocation()
    const isMobile = useCompacto(celular)
    const plano = usePlanoAtual(planoInicial)
    const [aberto, setAberto] = useState(null)
    const [gaveta, setGaveta] = useState(false)
    const [conta, setConta] = useState(false)
    const [rolou, setRolou] = useState(false)
    const refBarra = useRef(null)
    const unidadesPool = (() => { try { return (JSON.parse(localStorage.getItem('places')) || []).length } catch (e) { return 0 } })()
    const nomeLoja = ehConsole ? 'Equipe BIWAPP' : ehPool ? (localStorage.getItem('pool_nome') || 'Meu pool') : (localStorage.getItem('place_name') || 'Minha loja')
    const IconeConta = ehConsole ? IoShieldCheckmarkOutline : ehPool ? IoGridOutline : IoStorefrontOutline
    const subConta = ehConsole ? 'Admin Console' : ehPool ? 'Pool · ' + unidadesPool + (unidadesPool === 1 ? ' unidade' : ' unidades') : null

    // fecha tudo ao trocar de tela
    useEffect(() => { setAberto(null); setGaveta(false); setConta(false) }, [pathname])

    // clique fora / Esc fecham os menus
    useEffect(() => {
        const fora = (e) => { if (refBarra.current && !refBarra.current.contains(e.target)) { setAberto(null); setConta(false) } }
        const esc = (e) => { if (e.key === 'Escape') { setAberto(null); setConta(false); setGaveta(false) } }
        document.addEventListener('mousedown', fora)
        document.addEventListener('keydown', esc)
        return () => { document.removeEventListener('mousedown', fora); document.removeEventListener('keydown', esc) }
    }, [])

    useEffect(() => {
        const r = () => setRolou(window.scrollY > 4)
        window.addEventListener('scroll', r, { passive: true })
        return () => window.removeEventListener('scroll', r)
    }, [])

    // gaveta aberta: a página de trás não rola
    useEffect(() => {
        if (!gaveta) return undefined
        const antes = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => { document.body.style.overflow = antes }
    }, [gaveta])

    const comAtivo = grupos.map((g) => ({ ...g, itens: g.itens.map((i) => ({ ...i, ativo: ativoEm(i, pathname) })) }))
    // vitrine com `upgrade` conta: sem isso "Módulos" sumia do computador justo
    // para quem ainda não contratou (e a gaveta do celular mostrava)
    const naBarra = comAtivo.filter((g) => g.itens.some((i) => (!i.soon && !i.locked) || i.upgrade))
    const navegar = (i) => { onSelect && onSelect(i.key); setAberto(null); setGaveta(false) }
    const logo = isDarkMode ? '/images/biwapp-logo-white.svg' : '/images/biwapp-logo-black.svg'

    // a pílula do plano abre "Meu plano" (plano, faturas e comparação)
    // ícone pela família do plano (igual ao Admin Console): I.A. ✦, Conversão/Conversão+ 🚀, Premium ◆
    const IconePlano = /conver|turbo/i.test(plano || '') ? IoRocketOutline : /I\.A\./.test(plano || '') ? IoSparklesOutline : IoDiamondOutline
    const pilulaPlano = !semLoja && plano && <Link to="/plano" className="un-plano" title="Meu plano e faturas"><IconePlano size={14} /> {plano}</Link>

    const acoesConta = semLoja ? (
        <>
            <button type="button" className="un-conta-item" onClick={() => { setConta(false); setGaveta(false); onTrocarUnidade && onTrocarUnidade() }}><IoSwapHorizontalOutline size={17} /> Entrar em uma unidade</button>
            <button type="button" className="un-conta-item sair" onClick={onLogout}><IoLogOutOutline size={17} /> Sair</button>
        </>
    ) : (
        <>
            {/* trocar de unidade em primeiro: é o que a equipe/pool mais usa aqui */}
            {podeTrocarUnidade && <button type="button" className="un-conta-item" onClick={() => { setConta(false); setGaveta(false); onTrocarUnidade() }}><IoSwapHorizontalOutline size={17} /> Trocar de unidade</button>}
            <Link to="/plano" className="un-conta-item"><IoReceiptOutline size={17} /> Meu plano e faturas</Link>
            {isAdmin && <Link to="/developers" className="un-conta-item" onClick={() => onSelect && onSelect('developers')}><IoCodeSlashOutline size={17} /> Developers</Link>}
            {isAdmin && <button type="button" className="un-conta-item" onClick={onAdminConsole}><IoShieldCheckmarkOutline size={17} /> Admin Console</button>}
            <button type="button" className="un-conta-item sair" onClick={onLogout}><IoLogOutOutline size={17} /> Sair</button>
        </>
    )

    return (
        <div className={'pt un-nav' + (isDarkMode ? ' dark' : '')}>
            <header ref={refBarra} className={'un-barra' + (rolou ? ' rolou' : '')}>
                <Link to={ehConsole ? '/listlogins' : ehPool ? '/overview' : '/'} className="un-logo" onClick={() => onSelect && onSelect(ehConsole ? 'listlogins' : ehPool ? 'overview' : 'home')}><img src={logo} alt="BIWAPP" /></Link>

                {!isMobile && (
                    <nav className="un-links" aria-label="Menu principal">
                        {naBarra.map((g) => (
                            <Grupo key={g.title} grupo={g} aberto={aberto === g.title}
                                onAbrir={() => { setAberto(g.title); setConta(false) }} onFechar={() => setAberto(null)} onNavegar={navegar} />
                        ))}
                    </nav>
                )}

                <div className="un-dir">
                    {!isMobile && pilulaPlano}
                    <button type="button" className="un-icone" onClick={onToggleTheme} aria-label={isDarkMode ? 'Usar tema claro' : 'Usar tema escuro'} title={isDarkMode ? 'Tema claro' : 'Tema escuro'}>
                        {isDarkMode ? <IoSunnyOutline size={18} /> : <IoMoonOutline size={18} />}
                    </button>
                    {!isMobile && (
                        <div className={'un-conta' + (conta ? ' aberto' : '')}>
                            <button type="button" className="un-loja" onClick={() => { setConta(!conta); setAberto(null) }} aria-expanded={conta}>
                                <span className={'un-avatar' + (semLoja ? ' equipe' : '')}><IconeConta size={16} /></span>
                                <span className="un-loja-nome">{nomeLoja}</span>
                                <IoChevronDown size={13} className="un-seta" />
                            </button>
                            {conta && (
                                <div className="un-painel un-painel-conta" role="menu">
                                    <div className="un-conta-top">
                                        <span className={'un-avatar grande' + (semLoja ? ' equipe' : '')}><IconeConta size={20} /></span>
                                        <div><strong>{nomeLoja}</strong>{subConta ? <span>{subConta}</span> : plano && <span>Plano {plano}</span>}</div>
                                    </div>
                                    <div className="un-div" />
                                    {acoesConta}
                                </div>
                            )}
                        </div>
                    )}
                    {isMobile && (
                        <button type="button" className="un-icone un-menu" onClick={() => setGaveta(true)} aria-label="Abrir menu">
                            <IoMenuOutline size={22} />
                        </button>
                    )}
                </div>
            </header>

            {isMobile && gaveta && (
                <>
                    <div className="un-veu" onClick={() => setGaveta(false)} />
                    <aside className="un-gaveta" role="dialog" aria-label="Menu">
                        <div className="un-gaveta-top">
                            <span className={'un-avatar grande' + (semLoja ? ' equipe' : '')}><IconeConta size={20} /></span>
                            <div className="un-gaveta-loja"><strong>{nomeLoja}</strong>{subConta ? <span>{subConta}</span> : plano && <span>Plano {plano}</span>}</div>
                            <button type="button" className="un-icone" onClick={() => setGaveta(false)} aria-label="Fechar menu"><IoCloseOutline size={22} /></button>
                        </div>
                        <div className="un-gaveta-corpo">
                            {comAtivo.map((g) => (
                                <section key={g.title} className="un-secao">
                                    {!(g.itens.length === 1 && g.itens[0].solo) && <h4>{g.title}</h4>}
                                    {g.itens.map((i) => (
                                        <React.Fragment key={i.key}>
                                            {i.divider && <div className="un-div" />}
                                            <ItemMenu item={g.itens.length === 1 && i.solo ? { ...i, label: i.solo } : i} onNavegar={navegar} grande />
                                        </React.Fragment>
                                    ))}
                                </section>
                            ))}
                            <section className="un-secao">
                                <h4>Conta</h4>
                                <div className="un-conta-lista">{acoesConta}</div>
                            </section>
                        </div>
                    </aside>
                </>
            )}
        </div>
    )
}
